import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { BoardTag, DEFAULT_BOARD_TAGS } from "@/types";

export interface BrandConfig {
  name: string;
  language: string;
  market: string;
  boardTags: BoardTag[];
  writingEngine: string;
  doubaoModelEp: string;
}

const DEFAULT_CONFIG: BrandConfig = {
  name: "SEO Content Engine",
  language: "id",
  market: "Indonesia",
  boardTags: DEFAULT_BOARD_TAGS,
  writingEngine: "doubao",
  doubaoModelEp: (import.meta.env.VITE_DOUBAO_MODEL_EP as string | undefined) ?? "",
};

const LS_KEY = "seo_engine_brand_config";

function loadPersistedConfig(): BrandConfig {
  if (typeof window === "undefined") return DEFAULT_CONFIG;
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return DEFAULT_CONFIG;
    const parsed = JSON.parse(raw) as Partial<BrandConfig>;
    return {
      ...DEFAULT_CONFIG,
      ...parsed,
      boardTags:
        parsed.boardTags && parsed.boardTags.length > 0
          ? parsed.boardTags
          : DEFAULT_BOARD_TAGS,
    };
  } catch {
    return DEFAULT_CONFIG;
  }
}

interface BrandContextValue {
  config: BrandConfig;
  setConfig: (c: BrandConfig) => void;
}

const BrandContext = createContext<BrandContextValue>({
  config: DEFAULT_CONFIG,
  setConfig: () => {},
});

export function BrandProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfigState] = useState<BrandConfig>(DEFAULT_CONFIG);

  useEffect(() => {
    setConfigState(loadPersistedConfig());
  }, []);

  const setConfig = useCallback((c: BrandConfig) => {
    setConfigState(c);
    if (typeof window !== "undefined") {
      localStorage.setItem(LS_KEY, JSON.stringify(c));
    }
  }, []);

  return (
    <BrandContext.Provider value={{ config, setConfig }}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  return useContext(BrandContext);
}

export function useBoardTags(): BoardTag[] {
  const { config } = useBrand();
  return config.boardTags && config.boardTags.length > 0
    ? config.boardTags
    : DEFAULT_BOARD_TAGS;
}

export function findBoardTag(
  tags: BoardTag[],
  key: string
): BoardTag {
  return (
    tags.find((t) => t.key === key) ?? {
      key,
      label: key,
      bg: "bg-gray-100",
      text: "text-gray-600",
    }
  );
}
